import { t as uiText } from '../../i18n';
// src/features/workspace/fileStatsUtils.ts
import { 
  doc, 
  getDoc, 
  updateDoc, 
  runTransaction, 
  serverTimestamp, 
  collection, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { toRealDate } from '../../lib/realDate';
import { ensureFilesCollection } from '../files/fileUtils';

export interface FileStatsData {
  totalFileSize: number;
  fileCount: number;
  lastFileSizeUpdate: Date | null;
}

export interface UpdateFileStatsParams {
  workspaceId: string;
  sizeDelta: number; // プラスまたはマイナスのサイズ変更量
  countDelta: number; // プラスまたはマイナスのファイル数変更量
}

/**
 * ワークスペースのファイル統計をトランザクションで更新
 * メイン更新方法（提案1）
 * 
 * 注意: 現在はStorageファイル削除時の統計減算は無効化されています
 * 次フェーズでStorageファイル削除機能実装時に有効化予定
 */
export const updateWorkspaceFileStats = async ({ 
  workspaceId, 
  sizeDelta, 
  countDelta 
}: UpdateFileStatsParams): Promise<void> => {
  const workspaceRef = doc(db, 'workspaces', workspaceId);
  
  await runTransaction(db, async (transaction) => {
    const workspaceDoc = await transaction.get(workspaceRef);
    
    if (!workspaceDoc.exists()) {
      throw new Error(`Workspace ${workspaceId} not found`);
    }
    
    const currentData = workspaceDoc.data();
    const currentTotalSize = currentData.totalFileSize || 0;
    const currentFileCount = currentData.fileCount || 0;
    
    const newTotalSize = Math.max(0, currentTotalSize + sizeDelta);
    const newFileCount = Math.max(0, currentFileCount + countDelta);
    
    transaction.update(workspaceRef, {
      totalFileSize: newTotalSize,
      fileCount: newFileCount,
      lastFileSizeUpdate: serverTimestamp(),
    });
  });
};

/**
 * クエリによる実際のファイル統計を集計
 * バックアップ・検証用（提案4）
 */
export const calculateActualFileStats = async (workspaceId: string): Promise<FileStatsData> => {
  const filesCollection = ensureFilesCollection(workspaceId);
  const snapshot = await getDocs(filesCollection);
  
  let totalSize = 0;
  let fileCount = 0;
  
  snapshot.forEach((doc) => {
    const data = doc.data();
    const fileSize = data.size || 0;
    const refCount = data.referenceCount || 1;
    
    // 参照カウントが1以上のファイルのみカウント
    if (refCount > 0) {
      totalSize += fileSize;
      fileCount += 1;
    }
  });
  
  return {
    totalFileSize: totalSize,
    fileCount: fileCount,
    lastFileSizeUpdate: new Date(),
  };
};

/**
 * ワークスペースの現在のファイル統計を取得
 */
export const getWorkspaceFileStats = async (workspaceId: string): Promise<FileStatsData | null> => {
  const workspaceRef = doc(db, 'workspaces', workspaceId);
  const workspaceDoc = await getDoc(workspaceRef);
  
  if (!workspaceDoc.exists()) {
    return null;
  }
  
  const data = workspaceDoc.data();
  
  return {
    totalFileSize: data.totalFileSize || 0,
    fileCount: data.fileCount || 0,
    lastFileSizeUpdate: toRealDate(data.lastFileSizeUpdate),
  };
};

/**
 * 統計データの検証と修正
 * 不整合が発生した場合の修復機能（提案4）
 */
export const verifyAndFixFileStats = async (workspaceId: string): Promise<{
  wasFixed: boolean;
  beforeStats: FileStatsData | null;
  afterStats: FileStatsData;
}> => {
  const currentStats = await getWorkspaceFileStats(workspaceId);
  const actualStats = await calculateActualFileStats(workspaceId);
  
  // 統計が存在しない、または不整合がある場合は修正
  const needsFix = !currentStats || 
    currentStats.totalFileSize !== actualStats.totalFileSize ||
    currentStats.fileCount !== actualStats.fileCount;
  
  if (needsFix) {
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    await updateDoc(workspaceRef, {
      totalFileSize: actualStats.totalFileSize,
      fileCount: actualStats.fileCount,
      lastFileSizeUpdate: serverTimestamp(),
    });
    
    return {
      wasFixed: true,
      beforeStats: currentStats,
      afterStats: actualStats,
    };
  }
  
  return {
    wasFixed: false,
    beforeStats: currentStats,
    afterStats: actualStats,
  };
};

/**
 * ファイルサイズの人間が読みやすい形式への変換
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * ファイルサイズ制限チェック
 */
export const checkFileSizeLimit = (
  currentTotalSize: number,
  additionalSize: number,
  limitBytes: number = 100 * 1024 * 1024 // デフォルト100MB
): { allowed: boolean; reason?: string } => {
  const newTotalSize = currentTotalSize + additionalSize;
  
  if (newTotalSize > limitBytes) {
    return {
      allowed: false,
      reason: uiText("This exceeds the workspace file size limit ({{v0}}).\nCurrent: {{v1}} + added: {{v2}} = {{v3}}", { v0: formatFileSize(limitBytes), v1: formatFileSize(currentTotalSize), v2: formatFileSize(additionalSize), v3: formatFileSize(newTotalSize) })
    };
  }
  
  return { allowed: true };
};
